import type React from "react";
import { toast } from "sonner";
import type { AlertAction } from "@/components/Molecules/Alert/Alert";
import { Toast, type ToastColor } from "@/components/Molecules/Toast/Toast";
import {
	formatApiErrorToastDescription,
	getApiErrorDetail,
} from "@/utils/api-error-detail";

/**
 * Toast utility functions for custom JSX toasts using Sonner + DS Alert.
 */

/** Matches DS / Figma toast banner width; titles truncate within this width. */
export const TOAST_WIDTH_PX = 488;

/**
 * Stacking order for the global Sonner host.
 * Must sit above drawer/modal overlays (`z-50`) and app overlays that use `z-60`.
 */
export const TOAST_Z_INDEX = 100;

interface ToastOptions {
	message: string;
	/** Optional supporting text under the primary message. */
	description?: string;
	/**
	 * @deprecated No longer rendered — DS toast uses `message` as primary text.
	 */
	addonText?: string;
	duration?: number;
	position?:
		| "top-left"
		| "top-center"
		| "top-right"
		| "bottom-left"
		| "bottom-center"
		| "bottom-right";
	/**
	 * Whether Sonner allows dismissing the toast (swipe / timeout interaction).
	 * The Alert close button is always shown.
	 * @default true
	 */
	dismissible?: boolean;
	style?: React.CSSProperties;
	/** Optional primary action forwarded to DS Alert (e.g. Retry). */
	primaryAction?: AlertAction;
}

function showSemanticToast(
	color: ToastColor,
	{
		message,
		description,
		duration = 5000,
		position = "top-right",
		dismissible = true,
		style,
		primaryAction,
	}: ToastOptions,
): void {
	toast.custom(
		(t) => (
			<Toast
				id={String(t)}
				color={color}
				message={message}
				description={description}
				onClose={() => toast.dismiss(t)}
				primaryAction={
					primaryAction
						? {
								label: primaryAction.label,
								onPress: () => {
									toast.dismiss(t);
									primaryAction.onPress?.();
								},
							}
						: undefined
				}
			/>
		),
		{
			position,
			duration,
			dismissible,
			style: {
				width: TOAST_WIDTH_PX,
				maxWidth: "calc(100vw - 2rem)",
				...style,
			},
		},
	);
}

/**
 * Show a success toast
 */
export const showSuccessToast = (options: ToastOptions): void => {
	showSemanticToast("success", options);
};

/**
 * Show an error toast
 */
export const showErrorToast = (options: ToastOptions): void => {
	showSemanticToast("error", options);
};

export const showApiErrorToast = ({
	error,
	message,
	duration,
	...options
}: Omit<ToastOptions, "description" | "addonText"> & {
	error: unknown;
}): void => {
	const errorDetail = getApiErrorDetail(error);
	const description =
		errorDetail && errorDetail.toLowerCase() !== message.toLowerCase()
			? formatApiErrorToastDescription(errorDetail)
			: undefined;

	showErrorToast({
		...options,
		message,
		description,
		duration: duration ?? (description ? 10_000 : 5000),
	});
};

/**
 * Show a warning toast
 */
export const showWarningToast = (options: ToastOptions): void => {
	showSemanticToast("warning", options);
};

/**
 * Show an info toast
 */
export const showInfoToast = (options: ToastOptions): void => {
	showSemanticToast("info", options);
};

/**
 * Show a brand/primary toast
 */
export const showBrandToast = (options: ToastOptions): void => {
	showSemanticToast("brand", options);
};

/**
 * Show a gray/neutral toast
 */
export const showGrayToast = (options: ToastOptions): void => {
	showSemanticToast("gray", options);
};

/**
 * Show a custom toast with full control
 */
export const showCustomToast = ({
	content,
	duration = 5000,
	position = "top-right",
	dismissible = true,
}: {
	content: (toastId: string) => React.ReactNode;
	duration?: number;
	position?:
		| "top-left"
		| "top-center"
		| "top-right"
		| "bottom-left"
		| "bottom-center"
		| "bottom-right";
	dismissible?: boolean;
}): void => {
	toast.custom((t) => content(String(t)) as React.ReactElement, {
		position,
		duration,
		dismissible,
	});
};

/**
 * Promise-based toast for async operations
 */
export const showPromiseToast = <T,>({
	promise,
	loading,
	success,
	error,
}: {
	promise: Promise<T>;
	loading: { message: string; addonText?: string };
	success: { message: string; addonText?: string };
	error: { message: string; addonText?: string };
}) => {
	return toast.promise(promise, {
		loading: loading.message,
		success: () => {
			showSuccessToast({
				message: success.message,
			});
			return success.message;
		},
		error: () => {
			showErrorToast({
				message: error.message,
			});
			return error.message;
		},
	});
};
